import { useEffect, useState } from "react";
import styles from "./EditUS30Page.module.css";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { toast } from "react-toastify";

const EditUs30ContentPage = () => {
  /* ---------------- BASIC CONTENT ---------------- */

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [us30, setUs30] = useState({
    title: "",
    primaryText: "",
    secondaryText: "",

    bannerFile: null,
    bannerUrl: "",
    deleteBanner: false,

    founderImageFile: null,
    founderImageUrl: "",
    deleteFounderImage: false,
  });

  /* ---------------- VIDEO STATE ---------------- */

  const [memberships, setMemberships] = useState([]);
  const [videoLinks, setVideoLinks] = useState([]);
  const [editVideoIndex, setEditVideoIndex] = useState(-1);
  const [isVideoFree, setIsVideoFree] = useState(true);

  const [currentVideo, setCurrentVideo] = useState({
    title: "",
    url: "",
    videoType: "youtube",
    membershipIds: [],
    sortOrder: 0,
  });

  const [searchTextMembership, setSearchTextMembership] = useState("");

  /* ---------------- FETCH DATA ---------------- */

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [contentRes, membershipRes] = await Promise.all([
          api.get(ENDPOINTS.GET_US30_CONTENT),
          api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS),
        ]);

        if (contentRes.data.success) {
          const c = contentRes.data.content;

          setUs30({
            title: c.title_text ?? "",
            primaryText: c.description_text ?? "",
            secondaryText: c.secondary_text ?? "",

            bannerFile: null,
            bannerUrl: c.banner_image
              ? ENDPOINTS.IMAGE_BASE_URL + c.banner_image
              : "",
            deleteBanner: false,

            founderImageFile: null,
            founderImageUrl: c.founder_image
              ? ENDPOINTS.IMAGE_BASE_URL + c.founder_image
              : "",
            deleteFounderImage: false,
          });

          // Sort videos by sort_order when loading
          const videos = c.video_links || [];
          const sortedVideos = [...videos].sort(
            (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
          );
          setVideoLinks(sortedVideos);
        }

        if (membershipRes.data.success) {
          setMemberships(membershipRes.data.memberships);
        }
      } catch (e) {
        toast.error("Failed to load US30 content");
      }
    };

    fetchData();
  }, []);

  /* ---------------- IMAGE HANDLERS ---------------- */

  const handleImageUpload = (keyFile, keyUrl, deleteKey, e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUs30((prev) => ({
      ...prev,
      [keyFile]: file,
      [keyUrl]: URL.createObjectURL(file),
      [deleteKey]: false,
    }));
  };

  const handleImageDelete = (keyFile, keyUrl, deleteKey) => {
    setUs30((prev) => ({
      ...prev,
      [keyFile]: null,
      [keyUrl]: "",
      [deleteKey]: true,
    }));
  };

  /* ---------------- VIDEO LOGIC ---------------- */

  const resetVideoForm = () => {
    setCurrentVideo({
      title: "",
      url: "",
      videoType: "youtube",
      membershipIds: [],
      sortOrder: 0,
    });
    setIsVideoFree(true);
    setEditVideoIndex(-1);
  };

  // const handleAddOrUpdateVideo = () => {
  //   if (!currentVideo.url.trim()) {
  //     toast.error("Video URL is required");
  //     return;
  //   }

  //   if (!isVideoFree && currentVideo.membershipIds.length === 0) {
  //     toast.error("Select membership for paid video");
  //     return;
  //   }

  //   // Auto-detect video type
  //   let detectedType = "other";
  //   const url = currentVideo.url.trim();
  //   if (url.includes("youtube.com") || url.includes("youtu.be")) {
  //     detectedType = "youtube";
  //   } else if (url.includes("vimeo.com")) {
  //     detectedType = "vimeo";
  //   }

  //   const payload = {
  //     title: currentVideo.title?.trim() || null,
  //     url: url,
  //     videoType: detectedType,
  //     membershipIds: isVideoFree ? [] : currentVideo.membershipIds,
  //     sort_order:
  //       editVideoIndex === -1 ? videoLinks.length : currentVideo.sortOrder,
  //   };

  //   if (editVideoIndex === -1) {
  //     // Adding new video
  //     setVideoLinks((prev) => [...prev, payload]);
  //   } else {
  //     // Updating existing video
  //     setVideoLinks((prev) =>
  //       prev.map((v, i) => (i === editVideoIndex ? payload : v))
  //     );
  //   }

  //   resetVideoForm();
  // };
  const handleAddOrUpdateVideo = () => {
    if (!currentVideo.url.trim()) {
      toast.error("Video URL is required");
      return;
    }

    // Fix: Add null check
    const membershipIds = currentVideo.membershipIds || [];

    if (!isVideoFree && membershipIds.length === 0) {
      toast.error("Select membership for paid video");
      return;
    }

    // Auto-detect video type
    let detectedType = "other";
    const url = currentVideo.url.trim();
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      detectedType = "youtube";
    } else if (url.includes("vimeo.com")) {
      detectedType = "vimeo";
    }

    const payload = {
      title: currentVideo.title?.trim() || null,
      url: url,
      videoType: detectedType,
      membershipIds: isVideoFree ? [] : membershipIds,
      sort_order:
        editVideoIndex === -1 ? videoLinks.length : currentVideo.sortOrder,
    };

    if (editVideoIndex === -1) {
      // Adding new video
      setVideoLinks((prev) => [...prev, payload]);
    } else {
      // Updating existing video
      setVideoLinks((prev) =>
        prev.map((v, i) => (i === editVideoIndex ? payload : v))
      );
    }

    resetVideoForm();
  };

  const handleEditVideo = (index) => {
    const video = videoLinks[index];
    setCurrentVideo({
      ...video,
      membershipIds: video.membershipIds || [], // Add default empty array
      sortOrder: video.sort_order || index,
    });
    setIsVideoFree((video.membershipIds || []).length === 0);
    setEditVideoIndex(index);
  };

  const handleDeleteVideo = (index) => {
    setVideoLinks((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Reassign sort orders after deletion
      return updated.map((video, i) => ({
        ...video,
        sort_order: i,
      }));
    });
  };

  const handleMoveVideoUp = (index) => {
    if (index === 0) return;

    setVideoLinks((prev) => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [
        newList[index],
        newList[index - 1],
      ];

      // Update sort orders
      return newList.map((video, i) => ({
        ...video,
        sort_order: i,
      }));
    });
  };

  const handleMoveVideoDown = (index) => {
    if (index === videoLinks.length - 1) return;

    setVideoLinks((prev) => {
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [
        newList[index + 1],
        newList[index],
      ];

      // Update sort orders
      return newList.map((video, i) => ({
        ...video,
        sort_order: i,
      }));
    });
  };

  const handleMembershipToggle = (membership) => {
    setCurrentVideo((prev) => {
      const currentIds = prev.membershipIds || [];
      return {
        ...prev,
        membershipIds: currentIds.includes(membership.id)
          ? currentIds.filter((id) => id !== membership.id)
          : [...currentIds, membership.id],
      };
    });
  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      formData.append("title_text", us30.title);
      formData.append("description_text", us30.primaryText);
      formData.append("secondary_text", us30.secondaryText);

      if (us30.bannerFile) formData.append("banner_image", us30.bannerFile);
      if (us30.deleteBanner) formData.append("delete_banner_image", "true");

      if (us30.founderImageFile)
        formData.append("founder_image", us30.founderImageFile);
      if (us30.deleteFounderImage)
        formData.append("delete_founder_image", "true");

      if (videoLinks.length) {
        // Ensure videos are sorted before submitting
        const sortedVideos = videoLinks.map((v, i) => ({
          ...v,
          sort_order: i,
        }));
        formData.append("video_links", JSON.stringify(sortedVideos));
      }

      const { data } = await api.put(ENDPOINTS.GET_US30_CONTENT, formData);

      if (data.success) {
        toast.success("US30 content updated successfully");
      } else {
        toast.error(data.message || "Update failed");
      }
    } catch (e) {
      toast.error("Error updating US30 content");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredMemberships = memberships.filter((m) =>
    m.label.toLowerCase().includes(searchTextMembership.toLowerCase())
  );

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>Edit US30 Content</h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Banner */}
          <label className={styles.inlineLabel}>Banner Image</label>
          <div className={styles.featuredImageBox}>
            {us30.bannerUrl ? (
              <>
                <img
                  src={us30.bannerUrl}
                  alt="Banner Image"
                  className={styles.featuredImageBox}
                />
                <button
                  type="button"
                  disabled={isSubmitting}
                  title="Remove image"
                  className={styles.removeImageButton}
                  onClick={() =>
                    handleImageDelete("bannerFile", "bannerUrl", "deleteBanner")
                  }
                >
                  ✕
                </button>
              </>
            ) : (
              <div className={styles.iconPlaceholderContainer}>
                <img
                  src={placeholderImage}
                  alt="Placeholder"
                  className={styles.iconPlaceholder}
                />
                <label className={styles.uploadLink}>
                  <span className={styles.plusIcon}>+</span> Choose Banner Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) =>
                      handleImageUpload(
                        "bannerFile",
                        "bannerUrl",
                        "deleteBanner",
                        e
                      )
                    }
                    disabled={isSubmitting}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Title */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Title</label>
            <input
              type="text"
              value={us30.title}
              onChange={(e) => setUs30({ ...us30, title: e.target.value })}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>

          {/* Primary Description */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Primary Description</label>
            <textarea
              name="body_text"
              value={us30.primaryText}
              onChange={(e) =>
                setUs30({ ...us30, primaryText: e.target.value })
              }
              className={styles.textAreaLarge}
              rows="4"
              placeholder="Enter Primary Description"
              disabled={isSubmitting}
            />
          </div>

          {/* Founder Image */}
          <label className={styles.inlineLabel}>Founder Image</label>
          <div className={styles.featuredImageBox}>
            {us30.founderImageUrl ? (
              <>
                <img
                  src={us30.founderImageUrl}
                  alt="Founder Image"
                  className={styles.featuredImageBox}
                />
                <button
                  type="button"
                  disabled={isSubmitting}
                  title="Remove image"
                  className={styles.removeImageButton}
                  onClick={() =>
                    handleImageDelete(
                      "founderImageFile",
                      "founderImageUrl",
                      "deleteFounderImage"
                    )
                  }
                >
                  ✕
                </button>
              </>
            ) : (
              <div className={styles.iconPlaceholderContainer}>
                <img
                  src={placeholderImage}
                  alt="Placeholder"
                  className={styles.iconPlaceholder}
                />
                <label className={styles.uploadLink}>
                  <span className={styles.plusIcon}>+</span> Choose Founder
                  Image
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) =>
                      handleImageUpload(
                        "founderImageFile",
                        "founderImageUrl",
                        "deleteFounderImage",
                        e
                      )
                    }
                    disabled={isSubmitting}
                  />
                </label>
              </div>
            )}
          </div>

          {/* Secondary Description */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Secondary Text</label>
            <textarea
              name="body_text"
              value={us30.secondaryText}
              onChange={(e) =>
                setUs30({ ...us30, secondaryText: e.target.value })
              }
              className={styles.textAreaLarge}
              rows="4"
              placeholder="Enter Secondary Text"
              disabled={isSubmitting}
            />
          </div>
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          {/* ---------------- VIDEO LINKS ---------------- */}
          <div className={styles.sectionsWrapper}>
            <label className={styles.inlineLabel}>Video Links</label>

            <div className={styles.sectionsCard}>
              {/* Video Title */}
              <input
                type="text"
                placeholder="Video title (optional)"
                value={currentVideo.title ?? ""}
                onChange={(e) =>
                  setCurrentVideo({ ...currentVideo, title: e.target.value })
                }
                className={styles.textInput}
                disabled={isSubmitting}
              />

              {/* Video URL */}
              <input
                type="text"
                placeholder="Video URL"
                value={currentVideo.url}
                onChange={(e) =>
                  setCurrentVideo({ ...currentVideo, url: e.target.value })
                }
                className={styles.textInput}
                disabled={isSubmitting}
              />

              {/* Free / Paid Toggle */}
              <div className={styles.liveSessionRow}>
                <div className={styles.toggleWrapper}>
                  <button
                    className={`${styles.toggleOption} ${
                      isVideoFree ? styles.activeToggle : ""
                    }`}
                    onClick={() => setIsVideoFree(true)}
                    disabled={isSubmitting}
                  >
                    Free
                  </button>
                  <button
                    className={`${styles.toggleOption} ${
                      !isVideoFree ? styles.activeToggle : ""
                    }`}
                    onClick={() => setIsVideoFree(false)}
                    disabled={isSubmitting}
                  >
                    Paid
                  </button>
                </div>
              </div>

              {/* Select Memberships */}
              {!isVideoFree && (
                <div className={styles.sectionsCard}>
                  <div className={styles.searchBar}>
                    <input
                      type="text"
                      placeholder="type in to Filter memberships"
                      className={styles.searchInput}
                      value={searchTextMembership}
                      onChange={(e) => setSearchTextMembership(e.target.value)}
                      disabled={isSubmitting}
                    />
                    <span className={styles.searchIcon}>🔍</span>
                  </div>

                  <div className={styles.sectionList}>
                    {filteredMemberships.map((membership, i) => (
                      <div className={styles.sectionRow} key={i}>
                        <span className={styles.sectionLabel}>
                          {membership.label}
                        </span>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={(currentVideo.membershipIds || []).includes(
                            membership.id
                          )}
                          disabled={isSubmitting}
                          onChange={() => handleMembershipToggle(membership)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add / Update Button */}
              <button
                className={styles.submitButton}
                onClick={handleAddOrUpdateVideo}
                disabled={isSubmitting}
              >
                <span>
                  {editVideoIndex === -1 ? "Add Video" : "Update Video"}
                </span>
                <span className={styles.submitIcon}>
                  {editVideoIndex === -1 ? "+" : "✓"}
                </span>
              </button>
              <div className={styles.videoList}>
                {videoLinks.map((video, index) => (
                  <div key={index} className={styles.sectionRow}>
                    {/* Up/Down Arrows */}
                    <div className={styles.reorderButtons}>
                      <button
                        className={styles.arrowButton}
                        onClick={() => handleMoveVideoUp(index)}
                        disabled={index === 0 || isSubmitting}
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        className={styles.arrowButton}
                        onClick={() => handleMoveVideoDown(index)}
                        disabled={
                          index === videoLinks.length - 1 || isSubmitting
                        }
                        title="Move down"
                      >
                        ▼
                      </button>
                    </div>

                    {/* Video Title */}
                    <span className={styles.sectionLabel}>
                      {video.title || "Untitled Video"}
                    </span>

                    {/* Edit and Delete Actions */}
                    <div className={styles.sectionActions}>
                      <button
                        className={styles.editIconButton}
                        onClick={() => handleEditVideo(index)}
                        disabled={isSubmitting}
                      >
                        ✎
                      </button>
                      <button
                        className={styles.deleteIconButton}
                        onClick={() => handleDeleteVideo(index)}
                        disabled={isSubmitting}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            className={`${styles.submitButton} ${
              isSubmitting ? styles.submitting : ""
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            <>
              <span>
                {isSubmitting ? "Updating..." : "Update US30 Content"}
              </span>
              <span className={styles.submitIcon}>✓</span>
            </>
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditUs30ContentPage;
